/**
 * The four writes the creator makes, and the one key set they drop.
 *
 * · `POST /exercise` — 201 and the whole movement back, including its new id.
 * · `PATCH /exercise/{id}` — a partial write; every field left out is left alone. The wizard shows
 *   everything it submits, so it states them all rather than relying on that.
 * · `PUT /exercise/{id}/cover` — multipart, field name `file`, ≤10 MiB. An ordinary controller, so
 *   it goes through the same mutator with the same bearer; nothing special is needed for CORS.
 * · `DELETE /exercise/{id}` — 204, or the refusal `model/deleteOutcome` reads.
 *
 * ── The cover is a SECOND request, and it may fail without failing the save ─────────────────
 * It has to be: the endpoint is keyed on an id, so a new movement does not have one until the POST
 * has answered. That ordering has a consequence the wizard is honest about — the exercise is
 * already saved by the time the picture is refused, and telling the trainer "nie udało się zapisać"
 * would be a lie about work that is safely on the server. `saveExercise` therefore reports the
 * cover's failure BESIDE a successful save, in the phone's own sentence
 * (`mediaForm.coverUploadFailed` / `editWizard.coverUploadFailed`), rather than rejecting.
 *
 * The one thing it does not do is retry. A failed upload leaves the movement with whatever picture
 * it had, the trainer is told, and „Zmień okładkę" on the card is the way to try again — a loop
 * here would be a second place the upload is decided.
 *
 * ── The demo FILM is a third request, on the same terms ─────────────────────────────────────
 * `POST /exercise/{id}/demo-video/slot` → presigned PUT → `POST /media/{id}/confirm`, through
 * `~/shared/media/video/uploadVideo`, after the cover. Keyed on the id for the same reason, and a
 * failure is reported beside the save rather than failing it — with the backend's REASON, since a
 * film refused as too large will be refused again and „spróbuj ponownie” would be a lie. A film that
 * went up answers with its media id; the server transcodes it afterwards, and the page watches that.
 * The trainer may STOP the film on its way up („Przerwij wysyłanie”, `videoSignal`) until its PUT
 * has answered; that is its own outcome, `stopped`, and the save still resolves — the movement is on
 * the server either way.
 *
 * ── Neither write takes a content language, and that is the CONTRACT ────────────────────────
 * Library requirement R3: a custom exercise is never translated, so the endpoints that create or
 * modify one take no `lang` at all — what the trainer sends is what is stored and what comes back.
 * `postExercise` and `patchExerciseId` have no `params` argument for it, which is the generated
 * client saying the same thing. The catalogue is re-read afterwards anyway, and THAT read carries
 * the language.
 *
 * ── ONE invalidation, and it is the catalogue's root ────────────────────────────────────────
 * `exerciseLibraryRoot` is the prefix the catalogue, `similar` and `overlay` all hang under, so
 * dropping it redraws the table, the header's three figures, the preview panel and an open card
 * from one call. The same shape `api/materialsMutations` uses in the page slice, for the same
 * reason: an invalidation per key is three lists to remember together.
 */

import type { QueryClient } from '@tanstack/react-query';

import { uploadVideo } from '~/shared/media/video/uploadVideo';
import { abortReason, videoFailureKey } from '~/shared/media/video/videoFile';

import { clientRoot } from '~/entities/client';
import { exerciseLibraryRoot } from '~/entities/exercise-library';
import { planTemplateFullPrefix } from '~/entities/plan-draft';
import {
  deleteExerciseId,
  patchExerciseId,
  postExercise,
  postExerciseIdDemoVideoSlot,
  putExerciseIdCover,
  deleteExerciseIdReviewMark,
} from '~/shared/api/generated/endpoints/exercise/exercise';
import type {
  TrainWithMeModulesExercisesDtosRequestsPatchExerciseRequest,
  TrainWithMeModulesExercisesDtosRequestsPostExerciseRequest,
} from '~/shared/api/generated/model';

import { createExerciseRequest } from '../model/createRequest';
import { draftFromProposal, type DraftVideo, type ProposedExerciseFields } from '../model/exerciseDraft';
import type { VideoUploadOutcome } from '../model/saveNotice';

/** The files chosen this sitting, sent after the save. */
interface SaveExerciseMedia {
  readonly cover: File | null;
  /** Absent or `null`: no film this sitting. */
  readonly video?: DraftVideo | null;
  /** 0 to 1 while the film's bytes go up; called with 0 the moment its upload starts. */
  readonly onVideoProgress?: (fraction: number) => void;
  /** Stops the film's upload — and only that: the movement and its cover are saved by then. */
  readonly videoSignal?: AbortSignal;
  /** The film's bytes are up and its confirm has been sent for: `videoSignal` stops nothing now. */
  readonly onVideoSent?: () => void;
}

/** What one save is: a new movement or a correction to one, plus the picture chosen this sitting. */
export type SaveExerciseCommand =
  | (SaveExerciseMedia & {
      readonly mode: 'create';
      readonly body: TrainWithMeModulesExercisesDtosRequestsPostExerciseRequest;
    })
  | (SaveExerciseMedia & {
      readonly mode: 'edit';
      readonly exerciseId: number;
      readonly body: TrainWithMeModulesExercisesDtosRequestsPatchExerciseRequest;
    });

export interface SaveExerciseResult {
  /** The movement's id — the new one on a create, the same one on an edit. */
  readonly exerciseId: number;
  /** True when a picture was chosen and the upload did not take. The save itself succeeded. */
  readonly coverUploadFailed: boolean;
  readonly videoUpload: VideoUploadOutcome;
}

/**
 * Save the movement, then its picture.
 *
 * The order is forced on a create and is kept on an edit for one reason: the PATCH is what the
 * trainer pressed save on, and a cover upload that ran first would leave a new picture on a
 * movement whose other edits were then refused.
 */
export async function saveExercise(command: SaveExerciseCommand): Promise<SaveExerciseResult> {
  const saved =
    command.mode === 'create'
      ? await postExercise(command.body)
      : await patchExerciseId(command.exerciseId, command.body);

  const exerciseId = command.mode === 'edit' ? command.exerciseId : (saved.id ?? 0);
  // A create answered without an id is not a save this screen can stand on: there is no card to
  // open and no row to upload a cover to. It fails the save rather than opening card 0.
  if (exerciseId <= 0) throw new Error('POST /exercise answered without an id');

  const coverUploadFailed = await sendCover(exerciseId, command.cover);
  const videoUpload = await sendVideo(exerciseId, command);
  return { exerciseId, coverUploadFailed, videoUpload };
}

/**
 * „Szybkie dodanie" (ai-plan-builder R8): the movement Buddy proposed for a name outside the
 * catalogue, created as it stands and marked for review. Answers its id.
 *
 * Safe to send again after a failure the browser could not read: the server answers an identical
 * repeat with the movement the first one made, so a retry never adds the name twice. The catalogue
 * is the caller's to re-read, once for however many it added.
 */
export async function addProposedExercise(name: string, proposal: ProposedExerciseFields | null): Promise<number> {
  const { exerciseId } = await saveExercise({
    mode: 'create',
    body: createExerciseRequest(draftFromProposal(name, proposal)),
    cover: null,
  });
  return exerciseId;
}

/**
 * „Oznacz jako sprawdzone" (exercise-library R17): the owner takes the review mark off a movement
 * added from Buddy's proposal. Editing the movement never does; only this.
 */
export async function markExerciseChecked(exerciseId: number): Promise<void> {
  await deleteExerciseIdReviewMark(exerciseId);
}

/** True when a picture was chosen and did not take. */
async function sendCover(exerciseId: number, cover: File | null): Promise<boolean> {
  if (cover === null) return false;
  try {
    await putExerciseIdCover(exerciseId, { file: cover });
    return false;
  } catch {
    // Swallowed on purpose. See the header: the exercise IS saved, and the wizard says the picture
    // is the part that did not take.
    return true;
  }
}

async function sendVideo(
  exerciseId: number,
  media: SaveExerciseMedia,
): Promise<VideoUploadOutcome> {
  const { video, onVideoProgress, videoSignal: signal } = media;
  if (video == null) return { status: 'none' };
  onVideoProgress?.(0);
  try {
    const mediaId = await uploadVideo({
      requestSlot: (request, slotSignal) =>
        postExerciseIdDemoVideoSlot(exerciseId, request, undefined, slotSignal),
      file: video.file,
      durationSeconds: video.durationSeconds,
      onProgress: onVideoProgress,
      onSent: media.onVideoSent,
      signal,
    });
    return { status: 'sent', mediaId };
  } catch (error: unknown) {
    // Stopped only when the stop is what ended it: a confirm that failed after a late press (the
    // confirm is not stoppable, `uploadVideo`) is a failure like any other.
    if (signal?.aborted === true && error === abortReason(signal)) return { status: 'stopped' };
    // Swallowed like the cover's, and for the same reason — but with the backend's reason kept.
    return { status: 'failed', failureKey: videoFailureKey(error) };
  }
}

/** Replace the picture on a movement that already exists — „Zmień okładkę" on the card. */
export async function uploadExerciseCover(exerciseId: number, file: File): Promise<void> {
  await putExerciseIdCover(exerciseId, { file });
}

/** Remove the trainer's own movement. 204, or the refusal `model/deleteOutcome` reads. */
export async function deleteExercise(exerciseId: number): Promise<void> {
  await deleteExerciseId(exerciseId);
}

/**
 * After a DELETE: the catalogue, and everything else on the web that named the movement.
 *
 * The phone's `useDeleteExercise` (mobile #744) sweeps the client record feeds, the goals and the
 * plans, because backend #690 deletes the exercise's records, clips and client goals with it and
 * takes it out of finished and cancelled plan copies. The web reads three of those, and each is one
 * prefix: every client's detail (`clients / detail` — personal records, goal, muscle volume, trends),
 * and every plan's tree (`plan-template / full`). The catalogue's own root goes last and is the one
 * awaited, because it is what the screen redraws from.
 */
export async function invalidateAfterExerciseDelete(client: QueryClient): Promise<void> {
  void client.invalidateQueries({ queryKey: [...clientRoot, 'detail'] });
  void client.invalidateQueries({ queryKey: planTemplateFullPrefix });
  await invalidateExerciseLibrary(client);
}

/**
 * Drop everything that describes this catalogue.
 *
 * `await`ed by the caller, so a save stays pending until the list it is about to be read from has
 * actually arrived — otherwise the wizard closes onto a table that still shows the old row and it
 * changes under the trainer a beat later.
 */
export function invalidateExerciseLibrary(client: QueryClient): Promise<void> {
  return client.invalidateQueries({ queryKey: exerciseLibraryRoot });
}
